// Self-player bar — compact pill showing the local player's avatar,
// name, wallet, strike dots, and (when it's their turn) a draining
// timer ring.
//
// Previously the user could only infer their turn from the yellow
// "Your turn" toast; there was no countdown visible like the opponent
// seats had. This mirrors <Seat /> visually so turn-timer pacing feels
// consistent across the whole table — and surfaces the same strike
// counter so the user sees their own 3-strike-to-drop warning.

type Props = {
  name: string;
  avatarUrl?: string | null;
  wallet: number;
  isTurn: boolean;
  strikes?: number;
  strikeLimit?: number;
  // Bumped on every turn rotation so the CSS drain animation restarts
  // cleanly (React remounts the SVG via the `key` below).
  turnSeq: number;
};

export default function SelfBar({
  name, avatarUrl, wallet, isTurn, strikes = 0, strikeLimit = 3, turnSeq,
}: Props) {
  const dots = Math.max(0, Math.min(strikeLimit, strikes | 0));
  return (
    <div className={`rummy-selfbar ${isTurn ? 'rummy-selfbar--turn' : ''}`}>
      <div className="rummy-seat__avatar" style={{ width: 36, height: 36 }}>
        <svg
          key={`self-timer-${turnSeq}`}
          viewBox="0 0 56 56"
          className={`rummy-seat__ring rummy-timer-ring ${isTurn ? 'rummy-timer-ring--active' : ''}`}
        >
          <circle cx="28" cy="28" r="26" />
        </svg>
        {avatarUrl
          ? <img className="rummy-seat__img" src={avatarUrl} alt="" />
          : (
            <div
              className="rummy-seat__img"
              style={{
                background: '#22543d',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 900,
                color: '#fde08b',
                fontSize: 13,
              }}
            >
              {(name || '?').slice(0, 1).toUpperCase()}
            </div>
          )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
        <span className="rummy-selfbar__name">
          {name || 'You'}{isTurn ? ' · YOUR TURN' : ''}
        </span>
        <span className="rummy-selfbar__wallet">
          {wallet.toLocaleString('en-IN')}
        </span>
        {strikeLimit > 0 && (
          <div
            className="rummy-seat__strikes"
            aria-label={`${dots} of ${strikeLimit} strikes`}
            style={{ marginTop: 2 }}
          >
            {Array.from({ length: strikeLimit }).map((_, i) => (
              <span
                key={i}
                className={`rummy-seat__strike ${i < dots ? 'rummy-seat__strike--on' : ''}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
